class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) {
      overlay.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.close(event);
      });
    }
    this.setHeaderCartIconAccessibility();
    // Use event delegation for close buttons and product links
    // Use bubble phase (not capture) to allow buttons to handle their own events first
    this.addEventListener('click', this.handleDrawerClick.bind(this), false);
  }

  handleDrawerClick(event) {
    // First, check if this is a clickable element that should work normally
    const clickableElement = event.target.closest(
      'button, ' +
      'a, ' +
      'input, ' +
      'select, ' +
      'textarea, ' +
      'cart-remove-button, ' +
      '[role="button"], ' +
      '[tabindex="0"]'
    );
    
    // If it's a clickable element (except close button), let it through
    if (clickableElement && !clickableElement.closest('.drawer__close')) {
      // Allow the event to propagate normally
      return true;
    }
    
    // Handle close button clicks - use capture phase to catch it early
    const closeButton = event.target.closest('.drawer__close');
    if (closeButton) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      // Use setTimeout to ensure close happens after event is fully handled
      setTimeout(() => this.close(event), 0);
      return false;
    }
    
    // Also handle clicks on the SVG wrapper or icon inside the close button
    const svgWrapper = event.target.closest('.drawer__close .svg-wrapper');
    const svgElement = event.target.closest('.drawer__close svg');
    const spanElement = event.target.closest('.drawer__close span');
    
    if (svgWrapper || svgElement || spanElement) {
      const closeBtn = event.target.closest('.drawer__close');
      if (closeBtn) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        setTimeout(() => this.close(event), 0);
        return false;
      }
    }
    
    // Block product links (but not buttons or other interactive elements)
    const productLink = event.target.closest(
      '.cart-item__link, ' +
      '.cart-item__name a, ' +
      '.cart-item a[href*="/products/"]'
    );
    
    // Only block if it's actually a product link and not a button
    if (productLink && !productLink.closest('button') && productLink.tagName === 'A') {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return false;
    }
    
    // Allow all other events to proceed normally
    return true;
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close(event) {
    // Prevent any default behavior or navigation
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
    
    this.classList.remove('active', 'animate');
    // Don't refocus the active element to prevent navigation
    removeTrapFocus(null);
    document.body.classList.remove('overflow-hidden');
    
    // Clear any active element to prevent accidental navigation
    this.activeElement = null;
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      // Re-attach overlay click handler after content is rendered
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) {
        overlay.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.close(event);
        });
      }
      
      // Also add direct event listeners to close buttons for better reliability
      const closeButtons = this.querySelectorAll('.drawer__close');
      closeButtons.forEach((button) => {
        // Remove any existing listeners by cloning
        const newButton = button.cloneNode(true);
        button.parentNode.replaceChild(newButton, button);
        
        // Add fresh event listeners for both click and mousedown
        const handleClose = (event) => {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          this.close(event);
          return false;
        };
        
        newButton.addEventListener('click', handleClose, { capture: true });
        newButton.addEventListener('mousedown', handleClose, { capture: true });
        
        // Ensure button is always clickable
        newButton.style.pointerEvents = 'auto';
        newButton.style.zIndex = '1001';
        newButton.style.position = 'relative';
      });
      
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  constructor() {
    super();
    // Setup quantity validation respecting server-defined limits
    this.setupQuantityValidation();
  }

  setupQuantityValidation() {
    // Use MutationObserver to catch dynamically added quantity inputs
    const observer = new MutationObserver(() => {
      this.updateQuantityInputs();
    });

    observer.observe(this, {
      childList: true,
      subtree: true
    });

    // Initial setup
    this.updateQuantityInputs();
  }

  updateQuantityInputs() {
    const quantityInputs = this.querySelectorAll('input[type="number"][name="updates[]"], input[type="number"][name^="quantity"]');

    quantityInputs.forEach(input => {
      // Keep the max attribute if it exists (respects variant quantity rules from Shopify)
      // Only set minimum to 1 (never allow 0 in cart)
      const currentMin = parseInt(input.getAttribute('min')) || 0;
      if (currentMin < 1) {
        input.setAttribute('min', '1');
      }

      // Ensure current value is at least 1
      if (parseInt(input.value) < 1) {
        input.value = '1';
      }

      // Add input validation listener (use once to avoid duplicates)
      if (!input.dataset.quantityValidated) {
        input.dataset.quantityValidated = 'true';

        input.addEventListener('change', (e) => {
          const value = parseInt(e.target.value);
          const min = parseInt(e.target.min) || 1;
          const max = e.target.max ? parseInt(e.target.max) : null;

          if (isNaN(value) || value < min) {
            e.target.value = min.toString();
          } else if (max !== null && value > max) {
            e.target.value = max.toString();
          }
        });

        // Prevent typing 0 as first character
        input.addEventListener('keydown', (e) => {
          if (e.key === '0' && e.target.value === '') {
            e.preventDefault();
          }
        });
      }
    });
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);